import type {
    CommerceContext,
    CommerceEventUnsubscribe,
    CommerceOperationOptions,
    CommerceStorefrontChangedEvent,
    CommerceStorefrontChangedListener,
    CommerceStorefrontDomain,
    CommerceStorefrontExperience,
    ResolveCommerceStorefrontRequest,
    CommerceStorefrontAliasResolver,
} from "@teqtank/commerce-contracts";
import { DisposableListenerRegistry } from "../events/disposable-listener-registry.js";
import { CommerceStorefrontRepository } from "./commerce-storefront-repository.js";
export class CommerceStorefrontService implements CommerceStorefrontDomain {
    private readonly listeners = new DisposableListenerRegistry<CommerceStorefrontChangedEvent>();
    private value: CommerceStorefrontExperience;
    public constructor(
        private readonly repository: CommerceStorefrontRepository,
        context: CommerceContext,
        private readonly assertAvailable: () => void,
        private readonly resolveAlias: CommerceStorefrontAliasResolver,
    ) {
        this.value = Object.freeze({
            status: "generic",
            contact: null,
            attribution: Object.freeze({
                referrerId: null,
                webAlias: null,
                campaign: null,
                referrer: null,
                landingPage: null,
            }),
            requestedAlias: null,
            notice: null,
            marketId: context.market.id,
            currencyCode: context.currency.code,
            languageCode: context.locale.languageCode,
            resolvedAt: new Date(),
        });
    }
    public get current(): CommerceStorefrontExperience {
        return this.value;
    }
    public async resolve(
        request: ResolveCommerceStorefrontRequest = {},
        options: CommerceOperationOptions = {},
    ): Promise<CommerceStorefrontExperience> {
        this.assertAvailable();
        this.value = Object.freeze(await this.repository.resolve(request, options));
        this.listeners.emit(
            Object.freeze({
                reason: this.value.status === "unavailable" ? "unavailable" : "resolved",
                storefront: this.value,
            }),
        );
        return this.value;
    }

    public resolveLocation(
        location?: string | URL,
        attribution: Omit<ResolveCommerceStorefrontRequest, "url" | "webAlias"> = {},
        options: CommerceOperationOptions = {},
    ): Promise<CommerceStorefrontExperience> {
        const url =
            location instanceof URL
                ? location
                : new URL(
                      location ??
                          (typeof window === "undefined"
                              ? "https://commerce.invalid/"
                              : window.location.href),
                      "https://commerce.invalid/",
                  );
        const webAlias = this.resolveAlias(url);
        return this.resolve(
            { url: url.href, ...(webAlias ? { webAlias } : {}), ...attribution },
            options,
        );
    }

    public async clearAttribution(
        options: CommerceOperationOptions = {},
    ): Promise<CommerceStorefrontExperience> {
        this.assertAvailable();
        this.value = Object.freeze(await this.repository.clear(options));
        this.listeners.emit(Object.freeze({ reason: "cleared", storefront: this.value }));
        return this.value;
    }
    public updateContext(context: CommerceContext): void {
        this.value = Object.freeze({
            ...this.value,
            marketId: context.market.id,
            currencyCode: context.currency.code,
            languageCode: context.locale.languageCode,
            resolvedAt: new Date(),
        });
        this.listeners.emit(Object.freeze({ reason: "changed", storefront: this.value }));
    }
    public onChanged(listener: CommerceStorefrontChangedListener): CommerceEventUnsubscribe {
        return this.listeners.subscribe(listener);
    }
    public dispose(): void {
        this.listeners.release();
    }
}
